// Global price multiplier control + modal — shared by:
//   • Admin header (compact pill button + Apply modal)
//   • Settings → Pricing rules (full editor with audit history)
//
// Source of truth lives on the backend (`/api/admin/price-multiplier`).
// Frontend never persists; it only displays and submits. Confirmation
// modal is mandatory per TZ before applying a new value.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Sliders, X, Loader2, AlertTriangle, CheckCircle2, RotateCcw,
  TrendingUp, TrendingDown, History, Save,
} from 'lucide-react';
import { fetchWithStepUp } from './StepUpModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MIN = 0.5;
const MAX = 3.0;
const QUICK_PRESETS = [1.0, 1.05, 1.10, 1.15, 1.20];

// FastAPI returns `detail` either as a plain string OR as a structured
// object (step-up / MFA challenges) OR as a 422 validation array. Rendering
// the raw value produced the literal "[object Object]" the admin saw in
// production whenever MFA step-up kicked in. Always resolve to text.
function detailToText(detail, status) {
  if (!detail) return `HTTP ${status}`;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ') || `HTTP ${status}`;
  }
  if (typeof detail === 'object') {
    if (detail.code === 'mfa_required') {
      return 'Enable two-factor authentication on your admin account before changing prices (Settings → Security).';
    }
    if (detail.code === 'step_up_required') {
      return 'Confirmation with your authenticator app is required. Please enter the 6-digit code and try again.';
    }
    return detail.message || detail.detail || `HTTP ${status}`;
  }
  return `HTTP ${status}`;
}

function formatMul(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(n % 1 === 0 ? 2 : (n.toString().split('.')[1] || '').length > 2 ? 3 : 2);
}

function parseValue(raw) {
  if (raw === '' || raw == null) return NaN;
  const cleaned = String(raw).replace(',', '.').trim();
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Compact pill button for the admin top header. Clicking it opens the
 * confirmation modal. On mobile (< 640px) the same pill renders as an
 * icon-only button so it doesn't crowd the header.
 */
export function PriceMultiplierPill({ token }) {
  const [meta, setMeta]   = useState(null);
  const [open, setOpen]   = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/price-multiplier`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setMeta(await r.json());
    } catch (_) { /* silent */ }
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Poll lightly so a value changed by another admin propagates within ~30s.
  useEffect(() => {
    const id = setInterval(load, 120000);
    return () => clearInterval(id);
  }, [load]);

  const v = meta?.value ?? 1.0;
  const isCustom = Math.abs(v - 1.0) > 0.0005;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="price-multiplier-pill"
        title={`Global price multiplier: ${formatMul(v)}`}
        className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap ${
          isCustom
            ? 'bg-amber-400/20 hover:bg-amber-400/30 text-amber-200 border border-amber-300/40'
            : 'bg-white/10 hover:bg-white/20 text-slate-200 border border-white/15'
        }`}
      >
        <Sliders className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Price ×</span>
        <span className="font-bold" data-testid="price-multiplier-pill-value">{formatMul(v)}</span>
      </button>
      {open && (
        <PriceMultiplierModal
          token={token}
          current={meta}
          onClose={() => setOpen(false)}
          onSaved={() => { setRefreshKey((k) => k + 1); }}
        />
      )}
    </>
  );
}

/**
 * Confirmation modal / bottom sheet. Used by both the header pill and the
 * Settings page. Renders centered on desktop and as a full-width bottom
 * sheet on mobile so the form stays one-thumb reachable at 390px.
 */
export function PriceMultiplierModal({ token, current, onClose, onSaved }) {
  const startVal = current?.value ?? 1.0;
  const [draft, setDraft]   = useState(startVal.toString());
  const [note, setNote]     = useState('');
  const [error, setError]   = useState('');
  const [busy, setBusy]     = useState(false);
  const [confirming, setConfirming] = useState(false);

  const parsed = useMemo(() => parseValue(draft), [draft]);
  const isValid =
    Number.isFinite(parsed) && parsed >= MIN && parsed <= MAX;

  const change = parsed - startVal;
  const direction = !isValid ? null : Math.abs(change) < 0.0005 ? 'same' : change > 0 ? 'up' : 'down';

  const submit = async (finalValue) => {
    setError(''); setBusy(true);
    try {
      const r = await fetchWithStepUp(`${API_URL}/api/admin/price-multiplier`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: finalValue, note: note.trim() || null }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(detailToText(j.detail, r.status));
      onSaved && onSaved(j);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset the global price multiplier to 1.00?\n\nThis restores original catalog prices for new orders.')) return;
    setBusy(true); setError('');
    try {
      const r = await fetchWithStepUp(`${API_URL}/api/admin/price-multiplier/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(detailToText(j.detail, r.status));
      onSaved && onSaved(j);
      onClose();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="price-multiplier-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Sliders className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800">Global price multiplier</h3>
              <p className="text-xs text-slate-500 leading-snug">
                Scales catalog-based service prices for NEW orders only.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            data-testid="price-multiplier-modal-close"
            className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Current */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3 flex items-center justify-between text-xs">
          <div>
            <div className="text-slate-500">Current multiplier</div>
            <div className="text-xl font-black text-slate-800" data-testid="price-multiplier-current-value">×{formatMul(startVal)}</div>
          </div>
          {current?.updated_at && (
            <div className="text-right text-[10px] text-slate-500">
              <div>Updated {String(current.updated_at).slice(0, 16).replace('T', ' ')}</div>
              <div className="font-semibold">{current.updated_by}</div>
            </div>
          )}
        </div>

        {/* Quick presets */}
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500 mb-1.5">Quick presets</div>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => setDraft(p.toString())}
                data-testid={`price-multiplier-preset-${p.toFixed(2).replace('.', '')}`}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${
                  Math.abs(parsed - p) < 0.0005
                    ? 'bg-amber-500 text-white border-amber-500'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                }`}
              >
                ×{formatMul(p)}
              </button>
            ))}
          </div>
        </div>

        {/* Input */}
        <label className="block text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-1">
          New multiplier
        </label>
        <input
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setError(''); }}
          data-testid="price-multiplier-input"
          placeholder="1.00"
          className={`w-full text-2xl font-bold text-center py-2.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 ${
            !isValid && draft ? 'border-rose-400' : 'border-slate-300'
          }`}
        />
        <div className="mt-1 text-[11px] text-slate-500 text-center">
          Allowed range: ×{MIN.toFixed(2)} – ×{MAX.toFixed(2)}. Up to 3 decimals.
        </div>
        {!isValid && draft && (
          <div className="mt-2 text-xs text-rose-600 font-semibold" data-testid="price-multiplier-error">
            Enter a valid multiplier between {MIN.toFixed(2)} and {MAX.toFixed(2)}.
          </div>
        )}

        {/* Direction warning */}
        {isValid && direction === 'up' && (
          <div className="mt-3 p-2.5 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2 text-xs text-rose-700" data-testid="price-multiplier-direction-up">
            <TrendingUp className="w-4 h-4 mt-0.5 flex-shrink-0" />
            This will <b className="ml-1">increase</b> customer-facing prices for new orders by {((change / startVal) * 100).toFixed(1)}%.
          </div>
        )}
        {isValid && direction === 'down' && (
          <div className="mt-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2 text-xs text-emerald-700" data-testid="price-multiplier-direction-down">
            <TrendingDown className="w-4 h-4 mt-0.5 flex-shrink-0" />
            This will <b className="ml-1">reduce</b> customer-facing prices for new orders by {((Math.abs(change) / startVal) * 100).toFixed(1)}%.
          </div>
        )}

        {/* Worked example */}
        {isValid && direction !== 'same' && (
          <div className="mt-2 text-[11px] text-slate-500 text-center" data-testid="price-multiplier-example">
            Example: €100 base → <b className="text-slate-800">€{(100 * parsed).toFixed(2)}</b> at ×{formatMul(parsed)}
          </div>
        )}

        {/* Optional note */}
        <label className="block text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-1 mt-3">
          Reason (optional)
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="price-multiplier-note-input"
          placeholder="e.g. EU VAT bump for Q2"
          maxLength={200}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
        />

        {/* Errors */}
        {error && (
          <div className="mt-3 p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Confirmation step — only when valid AND value differs from current. */}
        {!confirming ? (
          <div className="mt-4 flex flex-wrap gap-2 justify-between">
            <button
              type="button"
              onClick={reset}
              disabled={busy || Math.abs(startVal - 1) < 0.0005}
              data-testid="price-multiplier-reset-btn"
              className="px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg flex items-center gap-1.5 disabled:opacity-40"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset to ×1.00
            </button>
            <div className="flex gap-2 ml-auto">
              <button
                type="button"
                onClick={() => !busy && onClose()}
                data-testid="price-multiplier-cancel-btn"
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setConfirming(true)}
                disabled={!isValid || direction === 'same' || busy}
                data-testid="price-multiplier-apply-btn"
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold disabled:opacity-50"
              >
                Apply…
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 leading-snug" data-testid="price-multiplier-confirm-message">
              <b className="block mb-1">Change global price multiplier?</b>
              Current multiplier: <b>×{formatMul(startVal)}</b>
              <br />New multiplier: <b>×{formatMul(parsed)}</b>
              <p className="mt-2">
                This will affect prices shown to customers and all newly created orders.
                Base catalog prices will not be changed. Existing orders will keep their already saved prices.
              </p>
            </div>
            <div className="mt-3 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={busy}
                data-testid="price-multiplier-confirm-cancel-btn"
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => submit(parsed)}
                disabled={busy}
                data-testid="price-multiplier-confirm-btn"
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Confirm change
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Settings → Prices → Global price multiplier (full editor + audit).
 */
export default function PriceMultiplierEditor({ token }) {
  const [meta, setMeta] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // Fetch the two endpoints independently — a failure on the audit
    // history (e.g. transient DB hiccup) must NOT keep the main editor
    // stuck on "Loading multiplier…" since the value itself loaded fine.
    try {
      const r = await fetch(`${API_URL}/api/admin/price-multiplier`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const j = await r.json();
        setMeta(j);
      } else {
        // Surface non-2xx as a usable empty state instead of an infinite spinner.
        setMeta({ value: 1.0, min: 0.5, max: 3.0, updated_at: null, _load_error: `HTTP ${r.status}` });
      }
    } catch (e) {
      setMeta({ value: 1.0, min: 0.5, max: 3.0, updated_at: null, _load_error: e?.message || 'network' });
    } finally {
      setLoading(false);
    }
    // Audit is best-effort — independent of the main panel.
    try {
      const r = await fetch(`${API_URL}/api/admin/price-multiplier/audit?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const j = await r.json();
        setAudit(j?.rows || []);
      }
    } catch (_) { /* keep prior audit state on failure */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading || !meta) {
    return (
      <div className="p-8 flex items-center gap-3 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading multiplier…
      </div>
    );
  }

  const isCustom = Math.abs(meta.value - 1.0) > 0.0005;

  return (
    <div className="space-y-4" data-testid="price-multiplier-editor">
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-amber-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
            <Sliders className="w-6 h-6 sm:w-7 sm:h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-slate-800">Global price multiplier</h2>
            <p className="text-xs sm:text-sm text-slate-600 leading-snug">
              Scales catalog-based service prices used by the New Order wizard.
              Base catalog prices are never overwritten — only the customer-facing display + new-order totals.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wide font-bold text-amber-700">Current</div>
            <div className="text-4xl font-black text-amber-700 leading-none" data-testid="price-multiplier-editor-value">×{formatMul(meta.value)}</div>
          </div>
        </div>
        {meta.updated_at && (
          <div className="mt-3 text-[11px] text-slate-500">
            Last changed {String(meta.updated_at).slice(0, 19).replace('T', ' ')} by <b>{meta.updated_by}</b>
            {meta.last_note && <> · &laquo;{meta.last_note}&raquo;</>}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="price-multiplier-editor-change-btn"
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> Change multiplier
          </button>
          {isCustom && (
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm('Reset the global price multiplier to 1.00?')) return;
                try {
                  await fetchWithStepUp(`${API_URL}/api/admin/price-multiplier/reset`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  load();
                } catch (_) { /* silent */ }
              }}
              data-testid="price-multiplier-editor-reset-btn"
              className="px-4 py-2 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" /> Reset to ×1.00
            </button>
          )}
        </div>
      </div>

      {/* Worked examples */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
        <h3 className="text-sm font-bold text-slate-800 mb-2">How it applies</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          {[50, 100, 250].map((base) => (
            <div key={base} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-slate-500">Base €{base}</div>
              <div className="font-bold text-slate-800 text-base">€{(base * meta.value).toFixed(2)}</div>
              <div className="text-slate-500 text-[10px]">at ×{formatMul(meta.value)}</div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-500 leading-snug">
          Does NOT apply to recharges, bonuses, balance adjustments, refunds, or any already-created order/invoice.
          Only NEW orders use the current multiplier.
        </p>
      </div>

      {/* Audit history */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
        <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" /> Change history
        </h3>
        {audit.length === 0 ? (
          <p className="text-xs text-slate-500">No changes recorded yet — current value is the default.</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr className="border-b border-slate-100">
                  <th className="text-left px-2 py-1.5 font-bold">When</th>
                  <th className="text-left px-2 py-1.5 font-bold">Admin</th>
                  <th className="text-left px-2 py-1.5 font-bold">Action</th>
                  <th className="text-left px-2 py-1.5 font-bold">From → To</th>
                  <th className="text-left px-2 py-1.5 font-bold">Note</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`price-multiplier-audit-${r.id}`}>
                    <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">{String(r.created_at).slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-2 py-1.5">{r.admin_email}</td>
                    <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      r.action === 'reset' ? 'bg-slate-100 text-slate-700' : 'bg-amber-100 text-amber-700'
                    }`}>{r.action}</span></td>
                    <td className="px-2 py-1.5 font-semibold">×{formatMul(r.old_value)} → ×{formatMul(r.new_value)}</td>
                    <td className="px-2 py-1.5 text-slate-500 max-w-[260px] truncate">{r.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <PriceMultiplierModal
          token={token}
          current={meta}
          onClose={() => setModalOpen(false)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
